import React, { useState, useEffect } from 'react';
import LandingShell from '@/components/landing/LandingShell';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Search, MessageSquare, Heart, Share2, Bookmark, ThumbsUp, Send, Bell, MoreVertical, Trash2, Edit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useForum } from '@/hooks/useForum';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ShareThreadDialog from '@/components/forum/ShareThreadDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '@/components/common/SEO';
import RichTextEditor from '@/components/ui/RichTextEditor';
import ForumPostContent from '@/components/forum/ForumPostContent';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  } else {
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  }
};

const Community = () => {
  const { toast } = useToast();
  const {
    categories,
    threads,
    posts,
    loading,
    fetchCategories,
    fetchThreads,
    fetchPosts,
    addThread,
    addReply,
    toggleLike,
    fetchLikes,
    fetchUserLiked,
    incrementThreadViews,
    editThread,
    editPost,
    deleteThread,
    deletePost,
  } = useForum();

  const { user } = useAuth();
  const { profile } = useProfile();

  const location = useLocation();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedThread, setSelectedThread] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [newThread, setNewThread] = useState({ title: '', content: '', category: '' });
  // NEW: keys to force RichTextEditor to re-sync after clearing
  const [replyEditorKey, setReplyEditorKey] = useState(0);
  const [perPostEditorKey, setPerPostEditorKey] = useState<{ [postId: string]: number }>({}); // 🛠️ FIX: Sync thread param only after threads have loaded
  const [showNewThread, setShowNewThread] = useState(false);
  const [likeState, setLikeState] = useState<{ [postId: string]: boolean }>({}); // 🛠️ FIX: Sync thread param only after threads have loaded
  const [likeCounts, setLikeCounts] = useState<{ [postId: string]: number }>({}); // 🛠️ FIX: Sync thread param only after threads have loaded
  const [activeReplyPostId, setActiveReplyPostId] = useState<string | null>(null);
  const [perPostReplyText, setPerPostReplyText] = useState<{ [postId: string]: string }>({}); // 🛠️ FIX: Sync thread param only after threads have loaded

  // --- Share dialog state ---
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareThreadTitle, setShareThreadTitle] = useState('');
  const [shareThreadUrl, setShareThreadUrl] = useState('');

  // --- Edit/Delete State ---
  const [editingThread, setEditingThread] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'thread' | 'post', id: string } | null>(null);

  // --- Follow state ---
  const [isFollowing, setIsFollowing] = useState(false);
  const [followId, setFollowId] = useState<string | null>(null);
  const [followersCount, setFollowersCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    if (loading) return;

    const params = new URLSearchParams(location.search);
    const threadParam = params.get('thread');

    if (threadParam && threads.find(thread => thread.id === threadParam)) {
      setSelectedThread(threadParam);
    } else {
      setSelectedThread(null);
    }
  }, [location.search, threads, loading]);

  // Keep URL in sync with selectedThread
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const threadParam = params.get('thread');

    if (selectedThread && selectedThread !== threadParam) {
      params.set('thread', selectedThread);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }

    if (!selectedThread && threadParam) {
      params.delete('thread');
      navigate(`${location.pathname}${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
    }
  }, [selectedThread]);

  useEffect(() => {
    if (selectedThread) {
      fetchPosts(selectedThread);
      incrementThreadViews(selectedThread);
      const thread = threads.find(t => t.id === selectedThread);
      if (thread) {
        setEditedTitle(thread.title);
      }
    } else {
      setEditingThread(false);
    }
  }, [selectedThread, threads]);

  useEffect(() => {
    if (posts.length > 0) {
      posts.forEach(async (post) => {
        const count = await fetchLikes(post.id);
        const liked = await fetchUserLiked(post.id);
        setLikeCounts(prev => ({ ...prev, [post.id]: count }));
        setLikeState(prev => ({ ...prev, [post.id]: liked }));
      });
    }
  }, [posts]);

  useEffect(() => {
    const fetchFollowState = async () => {
      if (!user || !selectedThread) return;

      const { data } = await supabase
        .from('forum_thread_follows')
        .select('id')
        .eq('user_id', user.id)
        .eq('thread_id', selectedThread)
        .maybeSingle();

      setIsFollowing(!!data?.id);
      setFollowId(data?.id ?? null);

      const { count } = await supabase
        .from('forum_thread_follows')
        .select('id', { count: 'exact', head: true })
        .eq('thread_id', selectedThread);

      setFollowersCount(count ?? 0);
    };

    if (selectedThread) fetchFollowState();
  }, [user, selectedThread]);

  const handleFollow = async () => {
    if (!user || !selectedThread) return;
    setFollowLoading(true);

    try {
      if (!isFollowing) {
        const { data, error } = await supabase
          .from('forum_thread_follows')
          .insert({ user_id: user.id, thread_id: selectedThread })
          .select()
          .single();
        if (error) throw error;

        setIsFollowing(true);
        setFollowId(data.id);
        setFollowersCount(c => c + 1);
        toast({ title: "Followed thread", description: "You'll be notified of updates." });
      } else {
        if (!followId) return;
        await supabase.from('forum_thread_follows').delete().eq('id', followId);

        setIsFollowing(false);
        setFollowId(null);
        setFollowersCount(c => Math.max(0, c - 1));
        toast({ title: "Unfollowed thread", description: "Notifications disabled." });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setFollowLoading(false);
    }
  };

  const handleLikePost = async (postId: string) => {
    const liked = likeState[postId];
    await toggleLike(postId, liked);
    setLikeState(prev => ({ ...prev, [postId]: !liked }));
    setLikeCounts(prev => ({ ...prev, [postId]: prev[postId] + (liked ? -1 : 1) }));
    toast({ title: liked ? "Like removed" : "Post liked", duration: 1500 });
  };

  const handleAddThread = async () => {
    const { title, content, category } = newThread;
    if (!title.trim() || !content.trim() || !category) {
      toast({ title: "Missing info", description: "Fill all fields.", variant: "destructive" });
      return;
    }

    const threadId = await addThread(title, category, content);
    if (threadId) {
      setSelectedThread(threadId);
      setShowNewThread(false);
      setNewThread({ title: '', content: '', category: '' });
      toast({ title: "Thread posted" });
    }
  };

  const handleAddReply = async () => {
    if (!replyText.trim() || !selectedThread) return;
    await addReply(selectedThread, replyText);
    setReplyText('');
    setReplyEditorKey((k) => k + 1); // NEW: force editor reset
    toast({ title: "Reply posted" });
  };

  const handleAddReplyToPost = async (postId: string) => {
    if (!selectedThread) return;
    const text = perPostReplyText[postId];
    if (!text?.trim()) return;
    await addReply(selectedThread, text);
    setPerPostReplyText(prev => ({ ...prev, [postId]: '' }));
    setPerPostEditorKey(prev => ({ ...prev, [postId]: (prev[postId] || 0) + 1 })); // NEW: force per-post editor reset
    setActiveReplyPostId(null);
    toast({ title: "Reply posted" });
  };

  const handleOpenShareDialog = () => {
    const thread = threads.find(t => t.id === selectedThread);
    if (!thread) return;
    setShareThreadTitle(thread.title);
    setShareThreadUrl(`${window.location.origin}${location.pathname}?thread=${thread.id}`);
    setShareDialogOpen(true);
  };

  const handleBackToAllThreads = () => {
    setSelectedThread(null);
    const params = new URLSearchParams(location.search);
    params.delete('thread');
    navigate(`${location.pathname}${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
  };

  const handleEditThread = async () => {
    if (!currentThread) return;
    const firstPost = posts[0];
    if (!firstPost) return;

    const threadSuccess = await editThread(currentThread.id, editedTitle);
    const postSuccess = await editPost(firstPost.id, editedContent, currentThread.id);

    if (threadSuccess && postSuccess) {
      setEditingThread(false);
      toast({ title: "Thread updated successfully" });
    }
  };

  const handleEditPost = async (postId: string) => {
    if (!selectedThread) return;
    const success = await editPost(postId, editedContent, selectedThread);
    if (success) {
      setEditingPostId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    let success = false;
    if (deleteTarget.type === 'thread') {
      success = await deleteThread(deleteTarget.id);
      if (success) {
        handleBackToAllThreads();
      }
    } else if (deleteTarget.type === 'post') {
      if (selectedThread) {
        success = await deletePost(deleteTarget.id, selectedThread);
      }
    }

    setDeleteTarget(null);
  };

  const filteredThreads = threads.filter(thread =>
    (selectedCategory === "all" || thread.category_id === selectedCategory) &&
    (!searchQuery || thread.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const currentThread = threads.find(t => t.id === selectedThread);
  const categoriesList = [{ id: "all", title: "All Topics" }, ...categories];

  if (loading) {
    return (
      <LandingShell>
        <div className="container mx-auto py-20 text-center text-muted-foreground">
          Loading forum...
        </div>
      </LandingShell>
    );
  }
  
  return (
    <LandingShell>
      <SEO
        title={selectedThread && currentThread ? currentThread.title : "MapleOS Forums"}
        description={selectedThread && currentThread && posts[0] 
          ? `Join the discussion: ${posts[0].content.substring(0, 160)}...` 
          : "Join discussions with fellow mapleos users and the core team. Share experiences, get help, and discover new ways to use the platform."
        }
        url={selectedThread ? `/forums?thread=${selectedThread}` : '/forums'}
      />
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">MapleOS Forums</h1>
            <p className="text-muted-foreground">
              Join discussions with fellow mapleos users and the core team. 
              Share your experiences, get help, and discover new ways to use the platform.
            </p>
          </div>
          {selectedThread === null ? (
            <>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search discussions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full sm:w-auto">
                  <TabsList className="w-full sm:w-auto">
                    {categoriesList.map(category => (
                      <TabsTrigger key={category.id} value={category.id} className="capitalize">
                        {category.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setShowNewThread(true)}>
                  New Thread
                </Button>
              </div>
              {/* New Thread Dialog */}
              {showNewThread && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>Create New Thread</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Input
                        placeholder="Thread title"
                        value={newThread.title}
                        onChange={e => setNewThread({ ...newThread, title: e.target.value })}
                      />
                      <RichTextEditor
                        value={newThread.content}
                        onChange={content => setNewThread({ ...newThread, content })}
                        placeholder="Thread content..."
                      />
                      <select
                        className="w-full p-2 rounded-md border border-input bg-background"
                        value={newThread.category}
                        onChange={e => setNewThread({ ...newThread, category: e.target.value })}
                      >
                        <option value="">Select category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.title}</option>
                        ))}
                      </select>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowNewThread(false)}>Cancel</Button>
                    <Button onClick={handleAddThread}>Post Thread</Button>
                  </CardFooter>
                </Card>
              )}
              <div className="space-y-4">
                {filteredThreads.map(thread => (
                  <Card 
                    key={thread.id} 
                    className={`hover:bg-muted/50 transition-colors cursor-pointer ${thread.is_pinned ? 'border-primary/30' : ''}`}
                    onClick={() => setSelectedThread(thread.id)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{thread.title}</CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={thread.user?.avatar || ""} alt={thread.user?.name || "User"} />
                              <AvatarFallback>{thread.user?.name?.charAt(0) || "U"}</AvatarFallback>
                            </Avatar>
                            <span>{thread.user?.name || "User"}</span>
                            {thread.user?.role === "admin" && (
                              <Badge variant="outline" className="text-blue-500 border-blue-200">Admin</Badge>
                            )}
                          </CardDescription>
                        </div>
                        <Badge className="bg-muted text-muted-foreground">{categories.find(c => c.id === thread.category_id)?.title || "General"}</Badge>
                      </div>
                    </CardHeader>
                    <CardFooter className="pt-0 flex justify-between text-sm text-muted-foreground">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-4 w-4" /> {thread.replies || 0}
                        </span>
                        <span>Views: {thread.views_count}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {thread.is_pinned && (
                          <Badge variant="outline" className="text-primary border-primary/20">Pinned</Badge>
                        )}
                        <span>Last activity: {formatDate(thread.updated_at)}</span>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            currentThread && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" onClick={handleBackToAllThreads}>← Back to all threads</Button>
                  <div className="flex gap-2 items-center">
                    {/* --- Follow/Unfollow Button --- */}
                    <Button 
                      size="sm" 
                      variant={isFollowing ? "default" : "outline"}
                      className={isFollowing ? "bg-green-600 text-white" : ""}
                      onClick={handleFollow}
                      disabled={followLoading || !user}
                    >
                      <Bell className="h-4 w-4 mr-1" />
                      {isFollowing ? "Unfollow" : "Follow"}
                    </Button>
                    <span className="text-xs text-muted-foreground">{followersCount} follower{followersCount === 1 ? '' : 's'}</span>
                    {/* --- Share Button --- */}
                    <Button size="sm" variant="outline" onClick={handleOpenShareDialog}>
                      <Share2 className="h-4 w-4 mr-1" /> Share
                    </Button>
                    {/* --- Thread Actions Dropdown --- */}
                    {(profile?.role === 'admin' || user?.id === currentThread.user_id) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {user?.id === currentThread.user_id && (
                            <DropdownMenuItem onClick={() => {
                              setEditingThread(true);
                              setEditedTitle(currentThread.title);
                              setEditedContent(posts[0]?.content || '');
                            }}>
                              <Edit className="mr-2 h-4 w-4" />
                              <span>Edit Thread</span>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteTarget({ type: 'thread', id: currentThread.id })}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            <span>Delete Thread</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
                <div>
                  {editingThread ? (
                    <Input
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="text-2xl font-bold mb-2 h-auto"
                    />
                  ) : (
                    <h1 className="text-2xl font-bold mb-2">{currentThread.title}</h1>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Badge className="bg-muted text-muted-foreground">{categories.find(c => c.id === currentThread.category_id)?.title || "General"}</Badge>
                    <span>Started {formatDate(currentThread.created_at)}</span>
                  </div>
                </div>
                <div className="space-y-6">
                  {posts.map((post, index) => (
                    <Card 
                      key={post.id} 
                      className="border"
                    >
                      <CardHeader className="pb-2 flex flex-row items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Avatar>
                            <AvatarImage src={post.user?.avatar || ""} alt={post.user?.name || "User"} />
                            <AvatarFallback>{post.user?.name?.charAt(0) || "U"}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{post.user?.name || "User"}</span>
                              {post.user?.role === "admin" && (
                                <Badge variant="outline" className="text-blue-500 border-blue-200">Admin</Badge>
                              )}
                            </div>
                            <span className="text-sm text-muted-foreground">{formatDate(post.created_at)}</span>
                          </div>
                        </div>
                        {(profile?.role === 'admin' || user?.id === post.user_id) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {user?.id === post.user_id && (
                                <DropdownMenuItem onClick={() => {
                                  setEditingPostId(post.id);
                                  setEditedContent(post.content);
                                }}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  <span>Edit Post</span>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => setDeleteTarget({ type: 'post', id: post.id })}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                <span>Delete Post</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </CardHeader>
                      <CardContent>
                        {editingPostId === post.id || (editingThread && index === 0) ? (
                          <RichTextEditor
                            value={editedContent}
                            onChange={setEditedContent}
                          />
                        ) : (
                          <ForumPostContent content={post.content} />
                        )}
                      </CardContent>
                      {editingPostId === post.id ? (
                        <CardFooter className="flex justify-end gap-2 py-3 border-t">
                          <Button variant="outline" size="sm" onClick={() => setEditingPostId(null)}>Cancel</Button>
                          <Button size="sm" onClick={() => handleEditPost(post.id)}>Save Changes</Button>
                        </CardFooter>
                      ) : editingThread && index === 0 ? null : (
                        <CardFooter className="flex justify-between text-sm border-t py-3">
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className={`gap-1 ${likeState[post.id] ? 'text-primary' : ''}`}

                              onClick={() => handleLikePost(post.id)}
                            >
                              <ThumbsUp className="h-4 w-4" /> 
                              {likeCounts[post.id] || 0}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="gap-1"
                              onClick={() => {
                                setActiveReplyPostId(post.id);
                                setPerPostReplyText(prev => ({ ...prev, [post.id]: '' }));
                              }}
                            >
                              <MessageSquare className="h-4 w-4" /> Reply
                            </Button>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Share2 className="h-4 w-4" />
                          </Button>
                        </CardFooter>
                      )}
                      {/* Per-post reply box */}
                      {activeReplyPostId === post.id && (
                        <div className="p-4 border-t bg-muted/20">
                          <RichTextEditor
                            value={perPostReplyText[post.id] || ''}
                            onChange={text => setPerPostReplyText(prev => ({ ...prev, [post.id]: text }))}
                            placeholder="Write your reply..."
                            // NEW: force reset on successful submit
                            contentKey={perPostEditorKey[post.id] || 0}
                          />
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              onClick={() => handleAddReplyToPost(post.id)}
                              disabled={!perPostReplyText[post.id] || !perPostReplyText[post.id].trim()}
                            >
                              <Send className="h-4 w-4 mr-1" /> Post Reply
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setActiveReplyPostId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>

                {editingThread && (
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditingThread(false)}>Cancel</Button>
                    <Button onClick={handleEditThread}>Save Thread Changes</Button>
                  </div>
                )}

                {/* Main reply box at the bottom */}
                {!editingThread && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Post a Reply</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RichTextEditor
                        value={replyText}
                        onChange={setReplyText}
                        placeholder="Write your reply here..."
                        // NEW: force reset on successful submit
                        contentKey={replyEditorKey}
                      />
                    </CardContent>
                    <CardFooter className="flex justify-between">
                      <Button variant="outline" onClick={() => setReplyText('')}>Cancel</Button>
                      <Button 
                        className="gap-2" 
                        disabled={!replyText.trim()}
                        onClick={handleAddReply}
                      >
                        <Send className="h-4 w-4" /> Post Reply
                      </Button>
                    </CardFooter>
                  </Card>
                )}
              </div>
            )
          )}
        </div>
      </div>
      {/* Share Thread Dialog */}
      <ShareThreadDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        threadTitle={shareThreadTitle}
        threadUrl={shareThreadUrl}
      />
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the {deleteTarget?.type}.
            </AlertDialogDescription>

          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className={buttonVariants({ variant: "destructive" })}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </LandingShell>
  );
};

export default Community;